// Navigation hint shown in the bottom bar, instead of a page number.
// - Adapts to how the visitor moves: a swipe animation on touch screens,
//   a scroll wheel with a mouse or trackpad, arrow keys once they use the keyboard.
// - Always names the next section ("Scroll for About"); the deck loops, so after
//   the last one comes Home again. Clicking it goes there.
// - Once the visitor has moved around a couple of times it shrinks to "About →",
//   and stays small on later visits.
// - Before the first move, the deck "peeks" at the next slide now and then.
// Also builds the section dots. Runs before the slider so they get its
// [data-goto] click handling and the first "slidechange".
const LEARNED_KEY = "hint-learned";
const LEARN_AFTER = 2; // moves before the hint shrinks
const PEEK_AFTER_MS = 5000;
const PEEK_EVERY_MS = 9000;
const MAX_PEEKS = 2;

const ACTIONS = { touch: "Swipe", wheel: "Scroll", keys: "Press →" };

export default function initHint() {
  const hint = document.getElementById("hint");
  const dotsNav = document.getElementById("dots");
  const track = document.getElementById("track");
  if (!hint || !track) return;
  const slides = [...track.querySelectorAll(".slide")];
  const action = hint.querySelector(".hint__action");
  const next = hint.querySelector(".hint__next");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let index = 0;
  let moves = -1; // the slider's first "slidechange" is the initial position, not a move
  let peeks = 0;
  let peekTimer;
  let mode = window.matchMedia("(pointer: coarse)").matches ? "touch" : "wheel";

  const learned = () => {
    try {
      return localStorage.getItem(LEARNED_KEY) === "1";
    } catch {
      return false;
    }
  };
  if (learned()) hint.classList.add("is-compact");

  // Section dots (no numbers); the active one stretches into a pill
  slides.forEach((slide) => {
    const dot = document.createElement("a");
    dot.href = `#${slide.id}`;
    dot.className = "dots__dot";
    dot.dataset.goto = "";
    dot.setAttribute("aria-label", slide.getAttribute("aria-label"));
    dot.dataset.label = slide.getAttribute("aria-label");
    dotsNav.append(dot);
  });
  const dots = [...dotsNav.children];

  function render() {
    hint.dataset.mode = mode;
    action.textContent = ACTIONS[mode];
    next.textContent = slides[(index + 1) % slides.length].getAttribute("aria-label");
    hint.setAttribute("aria-label", `Next section: ${next.textContent}`);
    dots.forEach((d, i) => {
      d.classList.toggle("is-active", i === index);
      d.setAttribute("aria-current", i === index ? "true" : "false");
    });
  }

  function setMode(m) {
    if (m === mode) return;
    mode = m;
    render();
  }

  window.addEventListener("touchstart", () => setMode("touch"), { passive: true });
  window.addEventListener("wheel", () => setMode("wheel"), { passive: true });
  window.addEventListener("keydown", (e) => {
    if (["ArrowRight", "ArrowLeft", "PageDown", "PageUp"].includes(e.key)) setMode("keys");
  });

  hint.addEventListener("click", () => {
    document.getElementById("next").click();
  });

  // Peek at the next slide while the visitor hasn't moved yet
  function schedulePeek(delay) {
    clearTimeout(peekTimer);
    if (reduceMotion || moves > 0 || peeks >= MAX_PEEKS || index !== 0) return;
    peekTimer = setTimeout(() => {
      peeks += 1;
      track.classList.add("is-peeking");
      hint.classList.add("is-nudging");
      setTimeout(() => {
        track.classList.remove("is-peeking");
        hint.classList.remove("is-nudging");
      }, 1400);
      schedulePeek(PEEK_EVERY_MS);
    }, delay);
  }

  document.addEventListener("slidechange", (e) => {
    index = e.detail.index;
    moves += 1;
    if (moves >= LEARN_AFTER && !hint.classList.contains("is-compact")) {
      hint.classList.add("is-compact");
      try {
        localStorage.setItem(LEARNED_KEY, "1");
      } catch {
        /* private mode: shrink for this visit only */
      }
    }
    render();
    if (moves > 0) clearTimeout(peekTimer);
    else schedulePeek(PEEK_AFTER_MS);
  });

  render();
}
